import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import { Icon } from "./Icon";
import { LabelText } from "./LabelText";
import { ThemedText } from "./ThemedText";

export type RadioOption<V = string> = {
  label: string;
  value: V;
  disabled?: boolean;
  content?: ReactNode;
};

export type RadioGroupProps<V = string> = {
  options: RadioOption<V>[];
  value: V | null;
  onChange: (value: V) => void;
  label?: string;
  error?: string | false;
  disabled?: boolean;
  required?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  optionLabelStyle?: StyleProp<TextStyle>;
};

type WebKeyDownEvent = {
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

const ICON_SIZE = 24;
const LABEL_GAP = 8;
const OPTION_GAP = 16;
const OPTION_MIN_HEIGHT = 44;

export function RadioGroup<V = string>({
  options,
  value,
  onChange,
  label,
  error,
  disabled = false,
  required = false,
  testID,
  style,
  labelStyle,
  optionLabelStyle,
}: RadioGroupProps<V>) {
  const { colors } = useCustomTheme();
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [focusedOption, setFocusedOption] = useState<string | null>(null);

  const select = (option: RadioOption<V>) => {
    if (disabled || option.disabled) return;
    onChange(option.value);
  };

  const requiredProps =
    required && !disabled && Platform.OS === "web"
      ? ({ "aria-required": true } as any)
      : undefined;

  return (
    <View
      testID={testID}
      style={[styles.group, style]}
      accessibilityRole="radiogroup"
      accessibilityLabel={label}
      {...requiredProps}
    >
      {label ? (
        <LabelText
          style={[{ color: colors.text }, labelStyle]}
          required={required}
          disabled={disabled}
        >
          {label}
        </LabelText>
      ) : null}

      {options.map((option) => {
        const optionKey = String(option.value);
        const isDisabled = disabled || option.disabled === true;
        const selected = value === option.value;
        const highlighted =
          !isDisabled &&
          (hoveredOption === optionKey || focusedOption === optionKey);
        const contentColor = isDisabled ? colors.muted : colors.text;
        const iconColor = isDisabled
          ? colors.muted
          : highlighted
            ? colors.palette.spinach40
            : colors.palette.spinach30;

        const keyboardProps = {
          onKeyDown: (event: WebKeyDownEvent) => {
            const key = event.nativeEvent?.key;
            if (key !== " " && key !== "Enter") return;
            event.preventDefault?.();
            select(option);
          },
        } as any;

        return (
          <Fragment key={optionKey}>
            <Pressable
              testID={testID ? `${testID}-option-${optionKey}` : undefined}
              accessibilityRole="radio"
              accessibilityLabel={option.label}
              aria-checked={selected}
              disabled={isDisabled}
              onPress={() => select(option)}
              onHoverIn={() => setHoveredOption(optionKey)}
              onHoverOut={() =>
                setHoveredOption((current) =>
                  current === optionKey ? null : current,
                )
              }
              onFocus={() => setFocusedOption(optionKey)}
              onBlur={() =>
                setFocusedOption((current) =>
                  current === optionKey ? null : current,
                )
              }
              {...keyboardProps}
              style={[
                styles.option,
                Platform.OS === "web"
                  ? ({ outlineStyle: "none" } as any)
                  : undefined,
                Platform.OS === "web" && isDisabled
                  ? ({ cursor: "not-allowed" } as any)
                  : undefined,
              ]}
            >
              <Icon
                iconSet="material-community"
                icon={selected ? "radiobox-marked" : "radiobox-blank"}
                size={ICON_SIZE}
                color={iconColor}
              />
              <ThemedText
                style={[
                  styles.optionLabel,
                  { color: contentColor },
                  optionLabelStyle,
                ]}
              >
                {option.label}
              </ThemedText>
            </Pressable>
            {selected ? option.content : null}
          </Fragment>
        );
      })}

      {error ? (
        <ThemedText
          testID={testID ? `${testID}-error` : undefined}
          accessibilityLiveRegion="polite"
          role="alert"
          style={[styles.error, { color: colors.danger }]}
        >
          {error}
        </ThemedText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  group: {
    width: "100%",
    gap: OPTION_GAP,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: LABEL_GAP,
    minHeight: OPTION_MIN_HEIGHT,
  },
  optionLabel: {
    flexShrink: 1,
  },
  error: {
    fontSize: 12,
    lineHeight: 14.4,
  },
});
